import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Grid, Snackbar, Alert, CircularProgress, FormControl, InputLabel, Select, MenuItem, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { createAmateur, updateAmateur } from '../data/AmateurData';


const AmateurEditModal = React.memo(({ open, onClose, initialData, onUpdate, isCreateMode = false, clubes = [] }) => {
  const [formData, setFormData] = React.useState({
    email: '',
    nombre: '',
    apellidos: '',
    club: '',
    sexo: '',
    fnacimiento: '',
    curp: '',
    telefono: '',
    celular: '',
    ghin: '',
    tipo: '',
    facebook: '',
    instagram: ''
  });
  const [showSuccess, setShowSuccess] = React.useState(false);
  const [validationError, setValidationError] = React.useState('');
  const [curpError, setCurpError] = React.useState('');
  const [ghinError, setGhinError] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [showError, setShowError] = React.useState(false);




  // Only update form data when modal opens with new initialData
  React.useEffect(() => {
    if (open && initialData) {
      const ghin = initialData.ghin || '';
      setFormData({
        email: initialData.email || '',
        nombre: initialData.nombre || '',
        apellidos: initialData.apellidos || '',
        club: initialData.club || '',
        sexo: initialData.sexo || '',
        fnacimiento: initialData.fnacimiento || '',
        curp: initialData.curp || '',
        telefono: initialData.telefono || '',
        celular: initialData.celular || '',
        ghin,
        tipo: '',
        handicapIndex: '0.0',
        fmg: '0',
        facebook: initialData.facebook || '',
        instagram: initialData.instagram || ''
      });
      setGhinError(ghin.length > 0 && ghin.length < 6 ? 'No es válido. Debe tener entre 6 y 10 caracteres' : '');
    }
  }, [open, initialData]);

  const validateAndExtractDateFromCURP = React.useCallback((curp, sexoParaValidar) => {
    if (curp.length !== 18) {
      setCurpError('La CURP debe tener 18 caracteres');
      return null;
    }

    // Extraer componentes de la CURP
    const year = curp.substring(4, 6);
    const month = curp.substring(6, 8);
    const day = curp.substring(8, 10);
    const sex = curp.substring(10, 11);

    // Validar el sexo en CURP (H = hombre, M = mujer)
    if (sex !== 'H' && sex !== 'M') {
      setCurpError('El sexo en la CURP no es válido');
      return null;
    }

    // Validar la fecha
    const fullYear = parseInt(year) < 50 ? '20' + year : '19' + year;
    const date = new Date(`${fullYear}-${month}-${day}`);
    
    if (isNaN(date.getTime())) {
      setCurpError('La fecha en la CURP no es válida');
      return null;
    }

    // Sexo para el formulario: H -> M (Masculino), M -> F (Femenino)
    const sexoForm = sex === 'H' ? 'M' : 'F';
    // Si se pasó sexo para validar, comprobar que coincida
    if (sexoParaValidar !== undefined && sexoParaValidar !== '' && sexoForm !== sexoParaValidar) {
      setCurpError('El sexo en la CURP no coincide con el sexo seleccionado');
      return null;
    }

    setCurpError('');
    return { birthDate: date.toISOString().split('T')[0], sexo: sexoForm };
  }, []);

  const handleChange = React.useCallback((e) => {
    const { name } = e.target;
    let value = e.target.value;
    // Limitar CURP a 18 caracteres
    if (name === 'curp') {
      value = value.slice(0, 18).toUpperCase();
      setCurpError('');
    }
    // Limitar GHIN a 10 caracteres y validar longitud mínima (6)
    if (name === 'ghin') {
      value = value.slice(0, 10);
      setGhinError(value.length > 0 && value.length < 6 ? 'No es válido. Debe tener entre 6 y 10 caracteres' : '');
    }
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      // Si el campo es CURP y tiene 18 caracteres, validar y actualizar fecha y sexo
      if (name === 'curp' && value.length === 18) {
        const result = validateAndExtractDateFromCURP(value);
        if (result) {
          newData.fnacimiento = result.birthDate;
          newData.sexo = result.sexo;
        }
      }
      return newData;
    });
  }, [validateAndExtractDateFromCURP]);

  const validateForm = React.useCallback(() => {
    setValidationError('');
    
    // Required fields validation
    const requiredFields = {
      nombre: 'Nombre',
      apellidos: 'Apellidos',
      club: 'Club',
      sexo: 'Sexo',
      fnacimiento: 'Fecha de Nacimiento',
      curp: 'CURP',
      ghin: 'GHIN',
    };

    for (const [field, label] of Object.entries(requiredFields)) {
      const value = formData[field];
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        setValidationError(`El campo "${label}" es obligatorio`);
        return false;
      }
    }

    // Validate CURP and extract birth date
    if (formData.curp) {
      const result = validateAndExtractDateFromCURP(formData.curp, formData.sexo);
      if (!result) {
        return false; // CURP validation failed
      }
      
      // Auto-fill birth date if it's empty or different
      if (!formData.fnacimiento || formData.fnacimiento !== result.birthDate) {
        setFormData(prev => ({
          ...prev,
          fnacimiento: result.birthDate
        }));
      }
    }

    // GHIN debe tener entre 6 y 10 caracteres
    if (formData.ghin && formData.ghin.length < 6) {
      setGhinError('No es válido. Debe tener entre 6 y 10 caracteres');
      return false;
    }

    // At least one phone number is required
    if (!formData.telefono && !formData.celular) {
      setValidationError('Debe proporcionar al menos un número de teléfono (teléfono o celular)');
      return false;
    }

    return true;
  }, [formData, validateAndExtractDateFromCURP]);

  const handleSubmit = React.useCallback(async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (isLoading) {
      return;
    }
    
    // Validate form before submitting
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      if (isCreateMode) {
        await createAmateur(formData);
      } else {
        await updateAmateur(formData);
      }
      if (onUpdate) {
        onUpdate();
      }
      setShowSuccess(true);
      setTimeout(() => {
        onClose();
        setShowSuccess(false);
      }, 1500);
    } catch (error) {
      console.error(`Error ${isCreateMode ? 'creating' : 'updating'} amateur profile:`, error);
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
      }, 3000);
    } finally {
      setIsLoading(false);
    }
  }, [formData, onUpdate, onClose, isCreateMode, validateForm, isLoading]);

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3
          }
        }}
      >
        <DialogTitle sx={{ 
          fontSize: '1.5rem',
          fontWeight: 'bold',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          {isCreateMode ? 'Registro de Jugador Amateur' : 'Editar Perfil Amateur'}
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {validationError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {validationError}
              </Alert>
            )}
            {showError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Error al {isCreateMode ? 'crear' : 'actualizar'}, inténtelo nuevamente
              </Alert>
            )}
            <Box sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    value={formData.email}
                    disabled
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Nombre"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Apellidos"
                    name="apellidos"
                    value={formData.apellidos}
                    onChange={handleChange}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth margin="normal" required>
                    <InputLabel>Club</InputLabel>
                    <Select
                      name="club"
                      value={formData.club}
                      onChange={handleChange}
                      label="Club"
                                                              >
                                                 {clubes.length === 0 ? (
                           <MenuItem disabled>No hay clubes disponibles</MenuItem>
                         ) : (
                           clubes.map((club) => (
                             <MenuItem key={club.id} value={club.id}>
                               {club.nombre}
                             </MenuItem>
                           ))
                         )}
                      </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="CURP"
                    name="curp"
                    value={formData.curp}
                    onChange={handleChange}
                    margin="normal"
                    inputProps={{ maxLength: 18 }}
                    error={!!curpError}
                    helperText={curpError}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="GHIN"
                    name="ghin"
                    value={formData.ghin}
                    onChange={handleChange}
                    margin="normal"
                    inputProps={{ maxLength: 10 }}
                    error={!!ghinError}
                    helperText={ghinError}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth margin="normal" required>
                    <InputLabel>Sexo</InputLabel>
                    <Select
                      name="sexo"
                      value={formData.sexo}
                      onChange={handleChange}
                      label="Sexo"
                    >
                      <MenuItem value="M">Masculino</MenuItem>
                      <MenuItem value="F">Femenino</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Fecha de Nacimiento"
                    name="fnacimiento"
                    type="date"
                    value={formData.fnacimiento}
                    onChange={handleChange}
                    margin="normal"
                    disabled
                    InputLabelProps={{
                      shrink: true,
                    }}
                    helperText="Se auto-completa desde la CURP"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Teléfono"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleChange}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Celular"
                    name="celular"
                    value={formData.celular}
                    onChange={handleChange}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Facebook"
                    name="facebook"
                    value={formData.facebook}
                    onChange={handleChange}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Instagram"
                    name="instagram"
                    value={formData.instagram}
                    onChange={handleChange}
                    margin="normal"
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button 
              onClick={onClose}
              disabled={isLoading}
              sx={{ 
                textTransform: 'none',
                borderRadius: 2,
                px: 3
              }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={20} /> : undefined}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                px: 3
              }}
            >
              {isLoading ? 'Guardando...' : (isCreateMode ? 'Registrar' : 'Guardar')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      
      <Snackbar
        open={showSuccess}
        autoHideDuration={1500}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowSuccess(false)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          {isCreateMode ? 'Jugador amateur registrado exitosamente' : 'Perfil amateur actualizado exitosamente'}
        </Alert>
      </Snackbar>
    </>
  );
});

export default AmateurEditModal;
